import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { fromEvent, merge, Observable, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { MuiCarouselSlide } from './mui-carousel-slide.component';
import { MuiI18nProvider } from '../i18n';

let nextUniqueId = 0;

@Component({
  selector: 'mui-carousel',
  templateUrl: './mui-carousel.component.html',
  styleUrls: ['./mui-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MuiCarousel implements AfterViewInit, AfterContentInit, OnDestroy {
  @Input()
  @HostBinding('attr.id')
  id: string | null = `mui-carousel-${nextUniqueId++}`;

  /** Should the component have a fixed height based on the largest slide? */
  @Input() fixedHeight = false;

  /** The minimum distance to drag before the next/previous slide will be selected */
  @Input() minSlideChangeDistance = 50;

  @ContentChildren(MuiCarouselSlide)
  slides!: QueryList<MuiCarouselSlide>;

  @HostBinding('class.mui-carousel')
  _baseClass = true;

  /**
   * Outputs the slide changed event and slide index
   */
  @Output() slideChanged = new EventEmitter<number>();

  @ViewChildren('slideContainer')
  private _slideContainers!: QueryList<ElementRef>;

  @ViewChild('slider', { static: true })
  private _slider!: ElementRef;

  get currentIndex(): number {
    return this._currentIndex;
  }

  set currentIndex(index: number) {
    if (index >= this.slides.toArray().length || index < 0) {
      throw new Error(`[MuiCarousel]: Cannot set index to ${index} because it is out of bounds.`);
    }

    if (this._currentIndex !== index) {
      this._currentIndex = index;
    }
  }

  private _currentIndex = 0;

  get dragging(): boolean {
    return this._dragStartX !== null;
  }

  protected readonly translations$: Observable<{ nextButton: string; previousButton: string }>;

  private _dragStartX: number | null = null;
  private _dragOffsetX = 0;
  private _destroy = new Subject<void>();

  constructor(private _cdr: ChangeDetectorRef, private _el: ElementRef, private _i18n: MuiI18nProvider) {
    this.translations$ = this._i18n.carousel();
  }

  /** Return the x coordinate value for MouseEvents and TouchEvents */
  private static eventXCoordinate($event: MouseEvent | TouchEvent): number {
    if ($event instanceof TouchEvent) {
      return $event.targetTouches[0].clientX;
    }

    return $event.clientX;
  }

  ngAfterViewInit(): void {
    this._cdr.detectChanges();
    this._registerEventHooks();
  }

  ngAfterContentInit(): void {
    if (this.slides && this.slides.first) {
      this.currentIndex = 0;
    }
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
  }

  /**
   * Run change detection.
   */
  detectChanges(): void {
    this._cdr.detectChanges();
  }

  /**
   * Jump to the previous slide within bounds.
   */
  next(): void {
    this.goToSlide(this._nextSlideIndex());
  }

  /**
   * Jump to the next slide within bounds.
   */
  previous(): void {
    this.goToSlide(this._previousSlideIndex());
  }

  goToSlide(index: number): void {
    if (index < 0 || index >= this.slides.length) {
      return;
    }

    const previousIndex = this.currentIndex;
    this.currentIndex = index;

    if (previousIndex !== this.currentIndex) {
      this.slideChanged.emit(this.currentIndex);
    }
  }

  /** Get the translateX position as a CSS transform string based on currentIndex and drag position */
  _getTranslatePos(): string {
    const slideWidth = this._slider.nativeElement.clientWidth;
    let offset = this.currentIndex * -slideWidth;

    if (this.dragging) {
      offset += this._dragOffsetX;
    }

    return 'translateX(' + offset + 'px)';
  }

  /** Calculate outer container's height based on the inner slides */
  _containerHeight(): number | null {
    if (!this._slideContainers) {
      return null;
    }

    let height: number;
    if (this.fixedHeight) {
      height = 0;
      this._slideContainers.toArray().forEach((current) => {
        height = Math.max(current.nativeElement.clientHeight, height);
      });
    } else {
      const currentSlideContainer = this._slideContainers.toArray()[this._currentIndex];
      height = currentSlideContainer.nativeElement.clientHeight;
    }

    return height;
  }

  getIndicatorId(index: number): string {
    return `${this.id}-indicator-${index}`;
  }

  getSlideId(index: number): string {
    return `${this.id}-slide-${index}`;
  }

  /** Register all event hooks. All events should cancel their subscription when the component is destroyed. */
  private _registerEventHooks(): void {
    // Drag event hooks
    merge(fromEvent<MouseEvent>(this._slider.nativeElement, 'mousedown'), fromEvent<TouchEvent>(this._slider.nativeElement, 'touchstart'))
      .pipe(takeUntil(this._destroy))
      .subscribe((event: MouseEvent | TouchEvent) => this._onDragStart(event));

    merge(fromEvent<MouseEvent>(document, 'mousemove'), fromEvent<TouchEvent>(document, 'touchmove'))
      .pipe(
        takeUntil(this._destroy),
        filter(() => this.dragging)
      )
      .subscribe((event: MouseEvent | TouchEvent) => this._onDrag(event));

    merge(fromEvent(document, 'mouseup'), fromEvent(document, 'touchend'))
      .pipe(
        takeUntil(this._destroy),
        filter(() => this.dragging)
      )
      .subscribe(() => this._onDragEnd());

    // Resize should trigger container size detection
    fromEvent(window, 'resize')
      .pipe(takeUntil(this._destroy), debounceTime(50))
      .subscribe(() => this._cdr.detectChanges());
  }

  /** Calculate the closest slide in the viewport based on the current drag position */
  private _getClosestSlideIndex(): number {
    const componentPosX = this._el.nativeElement.getBoundingClientRect().left;
    let slideIndex = this.currentIndex;
    let minDistance: number;

    this._slideContainers.forEach((slide, index) => {
      const distance = Math.abs(slide.nativeElement.getBoundingClientRect().left - componentPosX);

      if (isNaN(minDistance) || distance < minDistance) {
        minDistance = distance;
        slideIndex = index;
      }
    });

    return slideIndex;
  }

  /** Gets the previous slide index that is within bounds */
  private _previousSlideIndex(): number {
    return Math.max(0, this.currentIndex - 1);
  }

  /** Gets the next slide index that is within bounds */
  private _nextSlideIndex(): number {
    return Math.min(this.slides.length - 1, this.currentIndex + 1);
  }

  /** Calculate the slide index that will be selected after drag has ended */
  private _getDragEndSlideIndex(): number {
    const containerWidth = this._slider.nativeElement.clientWidth;
    const absDragDistance = Math.abs(this._dragOffsetX);

    if (absDragDistance >= containerWidth) {
      // If user has dragged a long distance, take the closest slide
      return this._getClosestSlideIndex();
    } else if (absDragDistance < containerWidth && absDragDistance >= this.minSlideChangeDistance) {
      // If drag distance was small, take next/previous slide
      return this._dragOffsetX < 0 ? this._nextSlideIndex() : this._previousSlideIndex();
    } else {
      // If drag distance was too small, do nothing at all
      return this.currentIndex;
    }
  }

  /** Drag Start event handler */
  private _onDragStart($event: MouseEvent | TouchEvent): boolean {
    if (!this.dragging) {
      this._dragStartX = MuiCarousel.eventXCoordinate($event);
      this._dragOffsetX = 0;
      this._cdr.detectChanges();
    }
    return false;
  }

  /** Drag Move event handler */
  private _onDrag($event: MouseEvent | TouchEvent): void {
    if (this.dragging) {
      this._dragOffsetX = MuiCarousel.eventXCoordinate($event) - (this._dragStartX ?? 0);
      this._cdr.detectChanges();
    }
  }

  /** Drag End event handler */
  private _onDragEnd(): void {
    if (this.dragging) {
      this.goToSlide(this._getDragEndSlideIndex());

      this._dragOffsetX = 0;
      this._dragStartX = null;
      this._cdr.detectChanges();
    }
  }
}
